import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton, CCollapse } from "@coreui/react";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { ReactComponent as CopyIcon } from "../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../assets/images/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useQuestions from "../hooks/useQuestions";
import useModalWindow from "../hooks/useModalWindow";
import { toast } from "react-toastify";

import {
  calculateInitialStdBlockIds,
  addStdPkBlock,
  removeStdPkBlock,
  duplicateStdPkBlock,
} from "./utils/standardPKUtils";

const StandardPKQuestions = ({ globalId, wizardForm }) => {
  const [stdBlockIds, setStdBlockIds] = useState(() =>
    calculateInitialStdBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...stdBlockIds, 0) + 1);
  const lastBlockRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const { questionsDataSaved } = useQuestions();
  const { modalWindow } = useModalWindow();
  // Force re-render after block controls (e.g. qPkTimelines for block 1) are ensured,
  // so QuestionsAnswersArray sees the control and renders consistently for the first block.
  const [, setBlocksControlsReady] = useState(false);
  // Tracks which accordion items are open as a Set of blockIds.
  // A Set is used so Expand all / Collapse all can open/close every item at once.
  const [openBlockIds, setOpenBlockIds] = useState(
    new Set([stdBlockIds[stdBlockIds.length - 1]])
  );

  // const dash = " - ";
  const dash = "";

  // Update Standard PK block IDs when form values are restored
  useEffect(() => {
    const newIds = calculateInitialStdBlockIds(wizardForm);
    const currentKey = stdBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setStdBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [wizardForm.value.qPkStdAnalysis, wizardForm.controls]);

  // Initialize controls for existing blocks (including first block's qPkTimelines)
  useEffect(() => {
    const isStdEnabled =
      wizardForm.controls.qPkStdEnabled?.value === "true" ||
      questionsDataSaved?.wizardForm?.qPkStdEnabled === "true";
    if (isStdEnabled) {
      stdBlockIds.forEach(blockId => {
        const suffix = blockId > 1 ? `_${blockId}` : "";

        const controlsToEnsure = [
          { name: `qPkTable${suffix}`, defaultValue: "" },
          { name: `qPkPopulation${suffix}`, defaultValue: "" },
          { name: `qPkMultiDose${suffix}`, defaultValue: "false" },
          { name: `qPkFigures${suffix}`, defaultValue: [] },
          { name: `qPkFiguresDescription${suffix}`, defaultValue: "" },
          { name: `qPkFiguresTimepoints${suffix}`, defaultValue: [] },
          { name: `qPkFiguresExtra${suffix}`, defaultValue: [] },
          { name: `qPkTimelines${suffix}`, defaultValue: [] },
          { name: `qPkStdColumn${suffix}`, defaultValue: [] },
          { name: `qPkStatsAnalysis${suffix}`, defaultValue: [] },
        ];

        controlsToEnsure.forEach(({ name, defaultValue }) => {
          if (!wizardForm.contains(name)) {
            if (
              name.startsWith("qPkTimelines") ||
              name.startsWith("qPkStdColumn") ||
              name.startsWith("qPkFiguresTimepoints")
            ) {
              wizardForm.addControl(name, new FormArray([]));
            } else {
              wizardForm.addControl(name, FormBuilder.control(defaultValue));
            }
          }
        });
      });
      // Re-render so QuestionsAnswersArray (e.g. qPkTimelines for block 1) sees the control
      setBlocksControlsReady(true);
    } else {
      setBlocksControlsReady(false);
    }
  }, [wizardForm, stdBlockIds, questionsDataSaved?.wizardForm?.qPkStdEnabled]);

  useEffect(() => {
    if (shouldScrollRef.current && lastBlockRef.current) {
      const el = lastBlockRef.current;
      // Walk up to find the named scroll container so we never touch the
      // browser viewport (scrollIntoView in Chrome scrolls the html element
      // even with overflow-y:hidden, hiding the logo and header bar).
      let wrapper = el.parentElement;
      while (
        wrapper &&
        !wrapper.classList.contains("dashboard-content__wrapper")
      ) {
        wrapper = wrapper.parentElement;
      }
      if (wrapper) {
        const elTop = el.getBoundingClientRect().top;
        const wrapperTop = wrapper.getBoundingClientRect().top;
        wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
      }
      shouldScrollRef.current = false;
    }
  }, [stdBlockIds.length]);

  const handleAddBlock = e => {
    console.log(e);
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    addStdPkBlock(wizardForm, newId);
    shouldScrollRef.current = true;
    setStdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
  };

  const handleDuplicateBlock = (e, displayIndex) => {
    if (e) e.preventDefault();
    const sourceBlockId = stdBlockIds[displayIndex];
    if (sourceBlockId === undefined) return;
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    duplicateStdPkBlock(wizardForm, sourceBlockId, newId);
    shouldScrollRef.current = true;
    setStdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
    toast.success("Block duplicated successfully");
  };

  const handleDuplicateLastBlock = e => {
    if (e) e.preventDefault();
    if (stdBlockIds.length === 0) return;
    handleDuplicateBlock(e, stdBlockIds.length - 1);
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (stdBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = stdBlockIds[displayIndex];
    removeStdPkBlock(wizardForm, blockId);
    const remainingIds = stdBlockIds.filter((_, i) => i !== displayIndex);
    setStdBlockIds(remainingIds);
    setOpenBlockIds(new Set([remainingIds[remainingIds.length - 1]]));
  };

  const renderBlockContent = (blockId, displayIndex) => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const isFirstBlock = displayIndex === 0;
    const blockNumber = displayIndex + 1;

    // Whole-number indexing: 1, 2, 3, 4, ... ; extra blocks use "2 - (blockNumber)." etc.
    const q = n => (isFirstBlock ? `${n}` : `${n}${dash}(${blockNumber})`);
    const questionIndex1a = q(2);
    const questionIndex1b = q(3);
    const questionIndex1c = q(4);
    const questionIndex1d = q(5);
    const questionIndex1d2 = isFirstBlock ? "5a" : `5a${dash}(${blockNumber})`;
    const questionIndex1d3 = isFirstBlock ? "5b" : `5b${dash}(${blockNumber})`;
    const questionIndex1e = q(6);
    const questionIndex1f = q(7);
    const questionIndex1g = q(8);
    const questionIndex1h = q(9);

    return (
      <>
        {wizardForm.controls.qPkStdEnabled?.value === "true" && (
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
            }}>
            <CButton
              color={"primary"}
              shape={"pill"}
              data-tooltip-id="duplicate-std-pk-block"
              style={{
                border: "1px solid #006dee",
                background: "#006dee",
                fontSize: "15px",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
              }}
              onClick={e => handleDuplicateBlock(e, displayIndex)}>
              <CopyIcon /> Duplicate
            </CButton>
            <Tooltip
              id="duplicate-std-pk-block"
              place="top"
              delayShow={100}
              className="custom-tooltip">
              Duplicate this block
            </Tooltip>
            {!isFirstBlock && (
              <>
                <CButton
                  color={"danger"}
                  shape={"pill"}
                  data-tooltip-id="remove-std-pk-block"
                  onClick={e => handleRemoveBlock(e, displayIndex)}>
                  <BinIcon />
                </CButton>
                <Tooltip
                  id="remove-std-pk-block"
                  place="top"
                  delayShow={100}
                  className="custom-tooltip">
                  Remove this block
                </Tooltip>
              </>
            )}
          </div>
        )}

        <QuestionItem
          questionIndex={questionIndex1a}
          questionName={`qPkTable${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1b}
          questionName={`qPkPopulation${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          questionInputType={"text"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1c}
          questionName={`qPkMultiDose${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          questionInputType={"radio"}
          questionInputChoice={"qPkMultiDose"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1d}
          questionName={`qPkFigures${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={false}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1d2}
          questionName={`qPkFiguresDescription${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        {wizardForm.controls[`qPkFiguresDescription${suffix}`]?.value && (
          <QuestionsAnswersArray
            questionIndex={questionIndex1d3}
            controlName={`qPkFiguresTimepoints${suffix}`}
            questionsName={"qPkFiguresTimepoints"}
            activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
            formGroup={wizardForm}
            inputTextBoxes={[["", "valueHelper", "text"]]}
            globalId={globalId}
          />
        )}
        <QuestionItem
          questionIndex={questionIndex1e}
          questionName={`qPkFiguresExtra${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={false}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1f}
          controlName={`qPkStdColumn${suffix}`}
          questionsName={"qPkStdColumn"}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "richtext"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1g}
          controlName={`qPkTimelines${suffix}`}
          questionsName={"qPkTimelines"}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1h}
          questionName={`qPkStatsAnalysis${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPkStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={true}
          globalId={globalId}
        />
      </>
    );
  };

  return (
    <>
      <QuestionItem
        questionIndex={"1"}
        questionName={"qPkStdEnabled"}
        activator={true}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {/* Standard PK Analysis Blocks */}
      {wizardForm.controls.qPkStdEnabled?.value === "true" && (
        <>
          {stdBlockIds.length === 1 ? (
            <div
              ref={lastBlockRef}
              className="analysis-block"
              // style={{ marginBottom: "2rem" }}
            >
              {renderBlockContent(stdBlockIds[0], 0)}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set(stdBlockIds))}>
                  Expand all
                </CButton>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set())}>
                  Collapse all
                </CButton>
              </div>
              <div className="accordion">
                {stdBlockIds.map((blockId, displayIndex) => {
                  const isOpen = openBlockIds.has(blockId);
                  return (
                    <div
                      key={`pk-block-${blockId}`}
                      className="accordion-item"
                      ref={
                        displayIndex === stdBlockIds.length - 1
                          ? lastBlockRef
                          : null
                      }>
                      <div className="accordion-header">
                        <button
                          type="button"
                          className={`accordion-button${isOpen ? "" : " collapsed"}`}
                          onClick={() =>
                            setOpenBlockIds(prev => {
                              const next = new Set(prev);
                              next.has(blockId)
                                ? next.delete(blockId)
                                : next.add(blockId);
                              return next;
                            })
                          }>
                          Block {displayIndex + 1}
                        </button>
                      </div>
                      <CCollapse
                        visible={isOpen}
                        className="accordion-collapse">
                        <div className="accordion-body">
                          <div
                            className="analysis-block"
                            // style={{ marginBottom: "2rem" }}
                          >
                            {renderBlockContent(blockId, displayIndex)}
                          </div>
                        </div>
                      </CCollapse>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {wizardForm.controls.qPkStdEnabled?.value === "true" && (
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "2rem",
                display: "flex",
                gap: "0.5rem",
              }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleAddBlock}>
                Add another standard PK analysis <PlusIcon />
              </CButton>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleDuplicateLastBlock}>
                Duplicate block above <CopyOutlineIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default StandardPKQuestions;
